import React, { useState, useEffect, useCallback } from 'react';
import { getAuth } from 'firebase/auth';
import { useToast } from '../../hooks/use-toast';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { Label } from '../ui/label';
import { ScrollArea } from '../ui/scroll-area';
import { Alert, AlertDescription, AlertTitle } from '../ui/alert';
import { Terminal, Plus, Search, AlertCircle, Undo2, Edit3 } from 'lucide-react';
import { Badge } from '../ui/badge';

const PracticeMgmtAdmin = () => {
  const auth = getAuth();
  const currentUser = auth.currentUser;
  const { toast } = useToast();

  const [practices, setPractices] = useState([]);
  const [originalPractices, setOriginalPractices] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredPractices, setFilteredPractices] = useState([]);
  const [selectedPractice, setSelectedPractice] = useState(null);
  const [editingPractice, setEditingPractice] = useState(null);
  
  const [isAuthorized, setIsAuthorized] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const [isSearching, setIsSearching] = useState(false);

  // State for adding a new practice
  const [showNewPracticeDialog, setShowNewPracticeDialog] = useState(false);
  const [newPracticeData, setNewPracticeData] = useState({
    prac_code: '',
    prac_name: '',
    address1: '',
    address2: '',
    address3: '',
    address4: '',
    postcode: '',
    icb: '',
    type: '',
    pcon: '',
    con_name: '',
    county: '',
    telephone: '',
    prac_type: ''
  });

  // Authorization check - simplified to just check if user exists
  useEffect(() => {
    const checkAdminStatus = async () => {
      if (currentUser) {
        try {
          const idTokenResult = await currentUser.getIdTokenResult();
          setIsAuthorized(!!idTokenResult.claims.admin);
        } catch (error) {
          console.error("Error fetching admin status:", error);
          setIsAuthorized(false);
          toast({ title: "Authorization Error", description: "Could not verify admin status.", variant: "destructive" });
        }
      } else {
        setIsAuthorized(false);
      }
      setLoading(false);
    };
    checkAdminStatus();
  }, [currentUser, toast]);

  // Load practices.json from public folder
  const loadPractices = useCallback(async () => {
    setLoading(true);
    try {
      const response = await fetch('/practices.json');
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const data = await response.json();
      const practicesArray = Array.isArray(data) ? data : [];
      setPractices(practicesArray);
      setOriginalPractices(JSON.parse(JSON.stringify(practicesArray))); // Deep copy for comparison
      toast({ title: "Practices Loaded", description: `Loaded ${practicesArray.length} practices.`, variant: "success" });
    } catch (error) {
      console.error("Error loading practices.json:", error);
      setPractices([]);
      setOriginalPractices([]);
      toast({ title: "Loading Error", description: "Could not load practices.json from public folder.", variant: "destructive" });
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => {
    if (isAuthorized) {
      loadPractices();
    }
  }, [isAuthorized, loadPractices]);

  // Debounced search effect with minimum 3 characters
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setIsSearching(true);
      
      if (searchTerm === '') {
        // Show top 20 practices alphabetically when no search term
        const top20 = practices
          .sort((a, b) => (a.prac_name || '').localeCompare(b.prac_name || ''))
          .slice(0, 20);
        setFilteredPractices(top20);
        console.log(`[PracticeMgmt] Showing top 20 practices (no search term)`);
      } else if (searchTerm.length >= 3) {
        // Only search after 3+ characters
        const lowerSearchTerm = searchTerm.toLowerCase();
        const filtered = practices.filter(
          (p) =>
            p.prac_name?.toLowerCase().includes(lowerSearchTerm) ||
            p.prac_code?.toLowerCase().includes(lowerSearchTerm) ||
            p.postcode?.toLowerCase().includes(lowerSearchTerm) ||
            p.address1?.toLowerCase().includes(lowerSearchTerm) ||
            p.address4?.toLowerCase().includes(lowerSearchTerm) ||
            p.con_name?.toLowerCase().includes(lowerSearchTerm)
        );
        setFilteredPractices(filtered);
        console.log(`[PracticeMgmt] Search for "${searchTerm}" found ${filtered.length} results`);
      } else {
        // Less than 3 characters - show empty list with hint
        setFilteredPractices([]);
        console.log(`[PracticeMgmt] Search term too short (${searchTerm.length} chars), showing empty list`);
      }
      
      setIsSearching(false);
    }, 300); // 300ms debounce

    return () => clearTimeout(timeoutId);
  }, [searchTerm, practices]);

  const handleSelectPractice = (practice) => {
    setSelectedPractice(practice);
    setEditingPractice({ ...practice });
  };

  const handleEditFieldChange = (field, value) => {
    setEditingPractice((prev) => ({ ...prev, [field]: value }));
  };

  // Calculate changes for tracking
  const getChanges = useCallback(() => {
    const changes = [];
    
    // Check for modified practices
    practices.forEach(practice => {
      const original = originalPractices.find(orig => orig.prac_code === practice.prac_code);
      if (original) {
        // Compare fields to find differences
        const modifiedFields = [];
        const fieldsToCheck = ['prac_name', 'address1', 'address2', 'address3', 'address4', 'postcode', 'county', 'telephone', 'icb', 'type', 'prac_type', 'pcon', 'con_name'];
        
        fieldsToCheck.forEach(field => {
          if ((original[field] || '') !== (practice[field] || '')) {
            modifiedFields.push({
              field,
              oldValue: original[field] || '',
              newValue: practice[field] || ''
            });
          }
        });
        
        if (modifiedFields.length > 0) {
          changes.push({
            type: 'modified',
            practice,
            original,
            modifiedFields
          });
        }
      } else {
        // This is a new practice
        changes.push({
          type: 'added',
          practice
        });
      }
    });
    
    return changes;
  }, [practices, originalPractices]);

  const changes = getChanges();
  const hasChanges = changes.length > 0;

  const handleSaveChanges = () => {
    if (!editingPractice || !selectedPractice) return;
    setIsSaving(true);
    
    setPractices((prevPractices) =>
      prevPractices.map((p) =>
        p.prac_code === selectedPractice.prac_code ? { ...editingPractice } : p
      )
    );
    
    toast({ 
      title: "Changes Saved (Locally)", 
      description: `Practice ${editingPractice.prac_name} updated. Download JSON to persist changes.`, 
      variant: "success" 
    });
    
    setSelectedPractice(null);
    setEditingPractice(null);
    setIsSaving(false);
  };

  // Generate new prac_code using the same logic as DataMergeTool
  const generateNewPracCode = useCallback(() => {
    console.log('[PracticeMgmt] Generating new practice code...');
    
    // Find all existing prac_codes that follow the "N#####" pattern
    const existingCodes = practices
      .filter(p => p.prac_code && typeof p.prac_code === 'string')
      .map(p => p.prac_code)
      .filter(code => /^N\d{5}$/.test(code)); // Only codes matching N##### pattern
    
    console.log(`[PracticeMgmt] Found ${existingCodes.length} existing N##### codes`);
    
    // Extract numeric parts and find the maximum
    const numericParts = existingCodes
      .map(code => parseInt(code.substring(1), 10))
      .filter(num => !isNaN(num));
    
    const maxNum = numericParts.length > 0 ? Math.max(...numericParts) : 0;
    const nextNum = maxNum + 1;
    const newCode = `N${nextNum.toString().padStart(5, '0')}`;
    
    console.log(`[PracticeMgmt] Generated new practice code: ${newCode} (next after ${maxNum})`);
    return newCode;
  }, [practices]);

  const handleNewPracticeFieldChange = (field, value) => {
    setNewPracticeData((prev) => ({ ...prev, [field]: value }));
  };

  const handleOpenNewPracticeDialog = () => {
    const newCode = generateNewPracCode();
    setNewPracticeData({
      prac_code: newCode,
      prac_name: '',
      address1: '',
      address2: '',
      address3: '',
      address4: '',
      postcode: '',
      icb: '',
      type: '',
      pcon: '',
      con_name: '',
      county: '',
      telephone: '',
      prac_type: ''
    });
    setShowNewPracticeDialog(true);
  };

  const handleSaveNewPractice = () => {
    if (!newPracticeData.prac_name || !newPracticeData.postcode) {
      toast({ 
        title: "Validation Error", 
        description: "Practice Name and Postcode are required.", 
        variant: "destructive" 
      });
      return;
    }
    
    setIsSaving(true);
    
    // Ensure prac_code is unique (regenerate if somehow duplicated)
    let finalCode = newPracticeData.prac_code;
    if (practices.some(p => p.prac_code === finalCode)) {
      finalCode = generateNewPracCode();
      console.log(`[PracticeMgmt] Code collision detected, using new code: ${finalCode}`);
    }
    
    const practiceToAdd = { ...newPracticeData, prac_code: finalCode };
    
    setPractices(prevPractices => [...prevPractices, practiceToAdd].sort((a, b) => 
      (a.prac_name || '').localeCompare(b.prac_name || '')
    ));
    
    toast({ 
      title: "Practice Added (Locally)", 
      description: `${practiceToAdd.prac_name} added with code ${finalCode}. Download JSON to persist.`, 
      variant: "success" 
    });
    
    setShowNewPracticeDialog(false);
    setIsSaving(false);
  };

  // Undo specific change
  const handleUndoChange = useCallback((change) => {
    if (change.type === 'modified') {
      // Restore the original practice
      setPractices(prev => 
        prev.map(p => 
          p.prac_code === change.practice.prac_code ? { ...change.original } : p
        )
      );
      toast({ 
        title: "Change Undone", 
        description: `Reverted changes to ${change.practice.prac_name}`, 
        variant: "success" 
      });
    } else if (change.type === 'added') {
      // Remove the added practice
      setPractices(prev => 
        prev.filter(p => p.prac_code !== change.practice.prac_code)
      );
      toast({ 
        title: "Addition Undone", 
        description: `Removed ${change.practice.prac_name}`, 
        variant: "success" 
      });
    }
    
    // Clear selection if we're undoing the currently selected practice
    if (selectedPractice?.prac_code === change.practice.prac_code) {
      setSelectedPractice(null);
      setEditingPractice(null);
    }
  }, [selectedPractice, toast]);

  // Edit a practice from the changes list
  const handleEditFromChanges = useCallback((practice) => {
    setSelectedPractice(practice);
    setEditingPractice({ ...practice });
  }, []);

  const downloadPracticesJson = () => {
    if (practices.length === 0) {
      toast({ title: "No Data", description: "No practices to download.", variant: "warning" });
      return;
    }
    
    const jsonString = JSON.stringify(practices, null, 2);
    const blob = new Blob([jsonString], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `practices-${new Date().toISOString().split('T')[0]}.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    
    toast({ 
      title: "Download Started", 
      description: "practices.json is being downloaded.", 
      variant: "success" 
    });
  };

  // Force refresh admin claim function
  const forceRefreshAdminClaim = async () => {
    if (currentUser) {
      try {
        await currentUser.getIdToken(true);
        const idTokenResult = await currentUser.getIdTokenResult();
        setIsAuthorized(!!idTokenResult.claims.admin);
        toast({ title: "Admin Claim Refreshed", variant: "success" });
      } catch (error) {
        console.error("Error forcing token refresh:", error);
        toast({ title: "Refresh Error", description: "Could not refresh admin claim.", variant: "destructive" });
      }
    }
  };

  return (
    <div className="p-4 sm:p-6 lg:p-8">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center justify-between">
            Practice Management (Admin)
            {hasChanges && (
              <Badge variant="secondary" className="bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-300">
                <AlertCircle className="h-3 w-3 mr-1" />
                {changes.length} unsaved change{changes.length !== 1 ? 's' : ''}
              </Badge>
            )}
          </CardTitle>
        </CardHeader>
        <CardContent>
          {!isAuthorized && (
            <Alert variant="destructive">
              <AlertTitle>Access Denied</AlertTitle>
              <AlertDescription>
                You do not have the necessary permissions to view this page.
                <Button onClick={forceRefreshAdminClaim} className="mt-2">Retry Authorization</Button>
              </AlertDescription>
            </Alert>
          )}
          {isAuthorized && (
            <div>
              <div className="flex flex-col sm:flex-row justify-between items-center mb-4 gap-2">
                <div className="relative flex-1 w-full sm:w-auto">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground h-4 w-4" />
                  <Input
                    placeholder="Search practices (name, code, postcode...)"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-10 flex-1 w-full"
                  />
                  {isSearching && (
                    <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                      <div className="animate-spin h-4 w-4 border-2 border-muted-foreground border-t-transparent rounded-full"></div>
                    </div>
                  )}
                </div>
                <div className="flex gap-2">
                  <Button onClick={loadPractices} variant="outline" disabled={loading}>
                    {loading ? 'Loading...' : 'Refresh List'}
                  </Button>
                  <Button onClick={handleOpenNewPracticeDialog} className="flex items-center gap-2">
                    <Plus size={16} /> Add New
                  </Button>
                </div>
              </div>
              {searchTerm.length > 0 && searchTerm.length < 3 && (
                <div className="text-sm text-muted-foreground mb-2">
                  Type at least 3 characters to search through {practices.length} practices.
                </div>
              )}

              {/* Changes Review Section */}
              {hasChanges && (
                <Card className="mb-4 border-orange-200 dark:border-orange-800">
                  <CardHeader className="py-3 px-4">
                    <CardTitle className="flex items-center gap-2 text-orange-700 dark:text-orange-300 text-md">
                      <AlertCircle className="h-5 w-5" />
                      Pending Changes ({changes.length})
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="p-4 space-y-3">
                    <div className="text-xs text-muted-foreground mb-2">
                      Review your changes before downloading. Click "Edit" to modify or "Undo" to revert.
                    </div>
                    <ScrollArea className="max-h-48">
                      <div className="space-y-2">
                        {changes.map((change, index) => (
                          <div key={`${change.practice.prac_code}-${index}`}
                               className="flex items-start justify-between p-2 border rounded-lg bg-muted/30 text-xs">
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-1 mb-0.5">
                                <Badge variant={change.type === 'added' ? 'default' : 'secondary'} className="text-[10px] px-1.5 py-0.5">
                                  {change.type === 'added' ? 'NEW' : 'MODIFIED'}
                                </Badge>
                                <span className="font-medium">{change.practice.prac_name}</span>
                                <span className="text-muted-foreground">({change.practice.prac_code})</span>
                              </div>
                              {change.type === 'modified' && (
                                <div className="text-muted-foreground space-y-0.5">
                                  {change.modifiedFields.slice(0, 2).map((field, i) => (
                                    <div key={i}>
                                      <span className="font-medium">{field.field}:</span>{' '}
                                      <span className="line-through text-red-500 dark:text-red-400">
                                        {field.oldValue || '(empty)'}
                                      </span>{' '}
                                      →{' '}
                                      <span className="text-green-500 dark:text-green-400">
                                        {field.newValue || '(empty)'}
                                      </span>
                                    </div>
                                  ))}
                                  {change.modifiedFields.length > 2 && (
                                    <div className="text-muted-foreground text-[10px]">
                                      ...and {change.modifiedFields.length - 2} more field{change.modifiedFields.length - 2 !== 1 ? 's' : ''}
                                    </div>
                                  )}
                                </div>
                              )}
                              {change.type === 'added' && (
                                <div className="text-muted-foreground">
                                  New: {change.practice.address1}, {change.practice.postcode}
                                </div>
                              )}
                            </div>
                            <div className="flex items-center gap-1 ml-2">
                              <Button variant="ghost" size="icon" className="h-6 w-6 p-0" onClick={() => handleEditFromChanges(change.practice)} title="Edit this practice">
                                <Edit3 className="h-3 w-3" />
                              </Button>
                              <Button variant="ghost" size="icon" className="h-6 w-6 p-0 text-orange-500 hover:text-orange-600" onClick={() => handleUndoChange(change)} title="Undo this change">
                                <Undo2 className="h-3 w-3" />
                              </Button>
                            </div>
                          </div>
                        ))}
                      </div>
                    </ScrollArea>
                  </CardContent>
                </Card>
              )}

              {loading && <p>Loading practices...</p>}
              {!loading && filteredPractices.length === 0 && searchTerm.length >=3 && (
                <p>No practices found for "{searchTerm}".</p>
              )}
              {!loading && practices.length > 0 && filteredPractices.length === 0 && searchTerm === '' && (
                 <p>Showing top 20 of {practices.length} practices. Use search to find more.</p>
              )}

              <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                {/* Practice List */}
                <Card className="lg:col-span-1">
                  <CardHeader className="p-3">
                    <CardTitle className="text-md">
                      Practices
                      {searchTerm === '' && practices.length > 0 && (
                        <span className="text-xs font-normal text-muted-foreground ml-1">
                          (Top 20 of {practices.length})
                        </span>
                      )}
                      {searchTerm.length >= 3 && (
                        <span className="text-xs font-normal text-muted-foreground ml-1">
                          ({filteredPractices.length} found)
                        </span>
                      )}
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="p-0">
                    <ScrollArea className="h-[600px] border-t">
                      {loading ? (
                        <div className="flex items-center justify-center py-8">
                           <div className="animate-spin h-5 w-5 border-2 border-muted-foreground border-t-transparent rounded-full"></div>
                           <span className="ml-2 text-sm text-muted-foreground">Loading...</span>
                        </div>
                      ) : searchTerm.length > 0 && searchTerm.length < 3 ? (
                        <div className="text-center text-muted-foreground py-8 px-2">
                          <Search className="h-10 w-10 mx-auto mb-1 opacity-50" />
                          <p className="text-sm">Type at least 3 characters</p>
                        </div>
                      ) : filteredPractices.length > 0 ? (
                        filteredPractices.map((p) => (
                          <div
                            key={p.prac_code}
                            className={`p-2.5 border-b cursor-pointer transition-colors text-sm ${
                              selectedPractice?.prac_code === p.prac_code
                                ? 'bg-primary text-primary-foreground'
                                : 'hover:bg-muted'
                            }`}
                            onClick={() => handleSelectPractice(p)}
                          >
                            <p className="font-semibold">{p.prac_name}</p>
                            <p className="text-xs text-muted-foreground">{p.prac_code} - {p.postcode}</p>
                            <p className="text-xs text-muted-foreground truncate">{p.address4 || p.county}</p>
                          </div>
                        ))
                      ) : practices.length === 0 && !loading ? (
                         <div className="text-center text-muted-foreground py-8 px-2">
                           <p className="text-sm">No practices loaded.</p>
                           <p className="text-xs mt-1">Try refreshing the list.</p>
                         </div>
                      ) : (
                        <div className="text-center text-muted-foreground py-8 px-2">
                          <Search className="h-10 w-10 mx-auto mb-1 opacity-50" />
                          <p className="text-sm">No practices found for "{searchTerm}"</p>
                        </div>
                      )}
                    </ScrollArea>
                  </CardContent>
                </Card>

                {/* Edit Form */}
                {editingPractice && selectedPractice && (
                  <Card className="lg:col-span-2">
                    <CardHeader className="p-3">
                      <CardTitle className="text-md">Edit: {selectedPractice.prac_name}</CardTitle>
                    </CardHeader>
                    <CardContent className="p-3 space-y-3">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
                        <div>
                          <Label htmlFor="prac_code_edit">Practice Code</Label>
                          <Input
                            id="prac_code_edit"
                            value={editingPractice.prac_code || ''}
                            disabled
                            className="bg-muted h-8 text-xs"
                          />
                        </div>
                        <div>
                          <Label htmlFor="prac_name_edit">Practice Name *</Label>
                          <Input
                            id="prac_name_edit"
                            value={editingPractice.prac_name || ''}
                            onChange={(e) => handleEditFieldChange('prac_name', e.target.value)}
                            className="h-8 text-xs"
                          />
                        </div>
                        <div>
                          <Label htmlFor="address1_edit">Address 1</Label>
                          <Input
                            id="address1_edit"
                            value={editingPractice.address1 || ''}
                            onChange={(e) => handleEditFieldChange('address1', e.target.value)}
                            className="h-8 text-xs"
                          />
                        </div>
                        <div>
                          <Label htmlFor="address2_edit">Address 2</Label>
                          <Input
                            id="address2_edit"
                            value={editingPractice.address2 || ''}
                            onChange={(e) => handleEditFieldChange('address2', e.target.value)}
                            className="h-8 text-xs"
                          />
                        </div>
                        <div>
                          <Label htmlFor="address3_edit">Address 3</Label>
                          <Input
                            id="address3_edit"
                            value={editingPractice.address3 || ''}
                            onChange={(e) => handleEditFieldChange('address3', e.target.value)}
                            className="h-8 text-xs"
                          />
                        </div>
                        <div>
                          <Label htmlFor="address4_edit">Town/City</Label>
                          <Input
                            id="address4_edit"
                            value={editingPractice.address4 || ''}
                            onChange={(e) => handleEditFieldChange('address4', e.target.value)}
                            className="h-8 text-xs"
                          />
                        </div>
                        <div>
                          <Label htmlFor="postcode_edit">Postcode *</Label>
                          <Input
                            id="postcode_edit"
                            value={editingPractice.postcode || ''}
                            onChange={(e) => handleEditFieldChange('postcode', e.target.value)}
                            className="h-8 text-xs"
                          />
                        </div>
                        <div>
                          <Label htmlFor="county_edit">County</Label>
                          <Input
                            id="county_edit"
                            value={editingPractice.county || ''}
                            onChange={(e) => handleEditFieldChange('county', e.target.value)}
                            className="h-8 text-xs"
                          />
                        </div>
                        <div>
                          <Label htmlFor="telephone_edit">Telephone</Label>
                          <Input
                            id="telephone_edit"
                            value={editingPractice.telephone || ''}
                            onChange={(e) => handleEditFieldChange('telephone', e.target.value)}
                            className="h-8 text-xs"
                          />
                        </div>
                        <div>
                          <Label htmlFor="icb_edit">ICB</Label>
                          <Input
                            id="icb_edit"
                            value={editingPractice.icb || ''}
                            onChange={(e) => handleEditFieldChange('icb', e.target.value)}
                            className="h-8 text-xs"
                          />
                        </div>
                        <div>
                          <Label htmlFor="type_edit">Type</Label>
                          <Input
                            id="type_edit"
                            value={editingPractice.type || ''}
                            onChange={(e) => handleEditFieldChange('type', e.target.value)}
                            className="h-8 text-xs"
                          />
                        </div>
                        <div>
                          <Label htmlFor="prac_type_edit">Practice Type</Label>
                          <Input
                            id="prac_type_edit"
                            value={editingPractice.prac_type || ''}
                            onChange={(e) => handleEditFieldChange('prac_type', e.target.value)}
                            className="h-8 text-xs"
                          />
                        </div>
                        <div>
                          <Label htmlFor="pcon_edit">PCON</Label>
                          <Input
                            id="pcon_edit"
                            value={editingPractice.pcon || ''}
                            onChange={(e) => handleEditFieldChange('pcon', e.target.value)}
                            className="h-8 text-xs"
                          />
                        </div>
                        <div>
                          <Label htmlFor="con_name_edit">Constituency Name</Label>
                          <Input
                            id="con_name_edit"
                            value={editingPractice.con_name || ''}
                            onChange={(e) => handleEditFieldChange('con_name', e.target.value)}
                            className="h-8 text-xs"
                          />
                        </div>
                      </div>
                      <div className="flex space-x-2 pt-2">
                        <Button onClick={handleSaveChanges} disabled={isSaving} size="sm">
                          {isSaving ? 'Saving...' : 'Save Changes'}
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => { setSelectedPractice(null); setEditingPractice(null); }}
                        >
                          Cancel
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                )}
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Add New Practice Dialog */}
      <Dialog open={showNewPracticeDialog} onOpenChange={setShowNewPracticeDialog}>
        <DialogContent className="sm:max-w-[600px] max-h-[90vh]">
          <DialogHeader>
            <DialogTitle>Add New Practice</DialogTitle>
          </DialogHeader>
          <ScrollArea className="max-h-[70vh] pr-5">
            <div className="grid gap-3 py-4 text-sm">
              {[
                { id: 'prac_code_new', label: 'Code', field: 'prac_code', disabled: true, className: "bg-muted" },
                { id: 'prac_name_new', label: 'Name*', field: 'prac_name' },
                { id: 'address1_new', label: 'Address 1', field: 'address1' },
                { id: 'address2_new', label: 'Address 2', field: 'address2' },
                { id: 'address3_new', label: 'Address 3', field: 'address3' },
                { id: 'address4_new', label: 'Town/City', field: 'address4' },
                { id: 'postcode_new', label: 'Postcode*', field: 'postcode' },
                { id: 'county_new', label: 'County', field: 'county' },
                { id: 'telephone_new', label: 'Telephone', field: 'telephone' },
                { id: 'icb_new', label: 'ICB', field: 'icb' },
                { id: 'type_new', label: 'Type', field: 'type' },
                { id: 'prac_type_new', label: 'Practice Type', field: 'prac_type' },
                { id: 'pcon_new', label: 'PCON', field: 'pcon' },
                { id: 'con_name_new', label: 'Constituency', field: 'con_name' },
              ].map(item => (
                <div key={item.id} className="grid grid-cols-4 items-center gap-3">
                  <Label htmlFor={item.id} className="text-right text-xs">{item.label}</Label>
                  <Input
                    id={item.id}
                    value={newPracticeData[item.field]}
                    onChange={(e) => handleNewPracticeFieldChange(item.field, e.target.value)}
                    disabled={item.disabled}
                    className={`col-span-3 h-8 text-xs ${item.className || ''}`}
                  />
                </div>
              ))}
            </div>
          </ScrollArea>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowNewPracticeDialog(false)} size="sm">
              Cancel
            </Button>
            <Button onClick={handleSaveNewPractice} disabled={isSaving} size="sm">
              {isSaving ? 'Adding...' : 'Add Practice'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Download and Deploy Instructions */}
      {isAuthorized && (
        <Card className="mt-6">
          <CardHeader className="p-3">
            <CardTitle className="text-md">Download and Deploy</CardTitle>
          </CardHeader>
          <CardContent className="p-3 space-y-3">
            <div className="flex items-center gap-3">
              <Button
                onClick={downloadPracticesJson}
                className="flex items-center gap-2"
                disabled={practices.length === 0}
                size="sm"
              >
                Download practices.json ({practices.length})
                {hasChanges && (
                  <Badge variant="secondary" className="ml-1.5 bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-300 text-[10px] px-1.5 py-0.5">
                    {changes.length} changes
                  </Badge>
                )}
              </Button>
              {hasChanges && (
                <div className="text-xs text-orange-600 dark:text-orange-400 flex items-center">
                  <AlertCircle className="inline h-3.5 w-3.5 mr-1" />
                  Unsaved changes exist
                </div>
              )}
            </div>
            <Alert className="text-xs">
              <Terminal className="h-3.5 w-3.5" />
              <AlertTitle className="text-sm">Manual Deployment Instructions</AlertTitle>
              <AlertDescription className="space-y-1.5">
                <p><strong>After downloading the practices.json file:</strong></p>
                <ol className="list-decimal list-inside pl-3 space-y-1">
                  <li>
                    <strong>Replace:</strong> Copy the downloaded file to your project's <code className="bg-muted px-1 py-0.5 rounded text-[11px]">public</code> folder, replacing <code className="bg-muted px-1 py-0.5 rounded text-[11px]">practices.json</code>.
                  </li>
                  <li>
                    <strong>Build:</strong> In your project terminal, run <code className="bg-muted px-1.5 py-0.5 rounded text-[11px]">npm run build</code>.
                  </li>
                  <li>
                    <strong>Deploy:</strong> Run <code className="bg-muted px-1.5 py-0.5 rounded text-[11px]">firebase deploy --only hosting</code>.
                  </li>
                  <li>
                    <strong>Verify:</strong> Check the live application.
                  </li>
                </ol>
              </AlertDescription>
            </Alert>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default PracticeMgmtAdmin;
